/**
 * Zustand store for reference-data manifest metadata.
 *
 * Persists to sessionStorage only (session-scoped, not cross-tab-persistent).
 * Key: 'wb-refdata-v1'.
 *
 * Only manifest-level data lives here (version + per-catalog ETags).
 * Catalog bodies are cached in TanStack Query via useRefCatalog.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { RefManifest } from '@/schemas/refData';

type RefDataState = {
  manifestVersion: string | null;
  catalogEtags: Record<string, string>;
};

type RefDataActions = {
  setManifest: (m: RefManifest) => void;
  clear: () => void;
  /** Clears all stored catalog ETags so the next manifest sync treats every catalog as new. */
  clearEtags: () => void;
};

export type RefDataStore = RefDataState & RefDataActions;

const initialState: RefDataState = {
  manifestVersion: null,
  catalogEtags: {},
};

export const useRefDataStore = create<RefDataStore>()(
  persist(
    (set) => ({
      ...initialState,

      setManifest: (m: RefManifest) => {
        // Invariant: catalog names in the manifest should be unique.
        // Object.fromEntries last-wins on duplicates; warn so it's visible.
        const names = m.catalogs.map((c) => c.name);
        const unique = new Set(names);
        if (unique.size !== names.length && import.meta.env.DEV) {
          console.warn(
            '[refDataStore] Duplicate catalog names in manifest — last-wins:',
            names.filter((n, i) => names.indexOf(n) !== i),
          );
        }
        set({
          manifestVersion: m.manifest_version,
          catalogEtags: Object.fromEntries(m.catalogs.map((c) => [c.name, c.etag])),
        });
      },

      clear: () => set(initialState),

      clearEtags: () => set({ catalogEtags: {} }),
    }),
    {
      name: 'wb-refdata-v1',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
